var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ArraySchema, Schema, MapSchema, type } from "@colyseus/schema";
import { DEFAULT_FACING_INDEX } from "./facing.js";
import { NO_GESTURE_INDEX } from "./gestures.js";
/**
 * Authoritative per-player state. The client never writes these directly — it
 * sends a move intent and the server walks the avatar toward it. Keeping this
 * asymmetry is what stops a modified client from teleporting or minting coins.
 */
export class Player extends Schema {
    constructor() {
        super(...arguments);
        this.sessionId = "";
        this.nickname = "";
        /** Current position, integrated by the server each tick. */
        this.x = 0;
        this.y = 0;
        /** Where the player clicked. The server walks (x,y) toward this. */
        this.targetX = 0;
        this.targetY = 0;
        /** Index into AVATAR_COLORS. */
        this.color = 0;
        /** True while the avatar is still travelling toward its target. */
        this.walking = false;
        /**
         * Índice en FACINGS. Lo decide el servidor a partir de dónde se hizo clic —no de
         * hacia dónde terminó caminando— para que estar pegado a un borde no impida
         * girar. Va en el estado para que todos vean al jugador mirando lo mismo.
         *
         * Los campos nuevos van al FINAL: el orden de los `@type` es el contrato binario
         * de Colyseus, así que insertar en el medio rompe la decodificación.
         */
        this.facing = DEFAULT_FACING_INDEX;
        /**
         * Ids de los ítems equipados, ya en orden de dibujo. Esto SÍ va al estado
         * sincronizado: es el único punto donde el progreso de un jugador se vuelve
         * visible para los demás — de nada sirve comprar un sombrero que nadie ve.
         *
         * Una lista en vez de un campo por ranura para que agregar ranuras no cambie el
         * contrato binario. El servidor la reconstruye siempre en orden de ranura.
         */
        this.equipped = new ArraySchema();
        /**
         * El objeto sobre cuya superficie está de pie, o vacío si está en el suelo.
         *
         * Va sincronizado porque los demás tienen que verte encima de la mesa: si fuera
         * local, cada cliente dibujaría a la misma persona a distinta altura y el orden de
         * dibujo dejaría de coincidir. Es un id y no un booleano porque de él salen la
         * altura y el polígono, y ambos viven en el catálogo.
         *
         * Al final de la clase, como manda el contrato binario.
         */
        this.onElement = "";
        /**
         * Id de la cuenta detrás del avatar. Es un IDENTIFICADOR público, no un secreto:
         * sin él, mandar una solicitud de amistad desde la tarjeta de perfil exigiría
         * adivinar a quién apunta un `sessionId`, que cambia en cada conexión.
         *
         * Al final de la clase, como manda el contrato binario.
         */
        this.accountId = "";
        /**
         * Clubes a los que pertenece, para dibujar sus insignias sin pedir nada por HTTP.
         *
         * Ids de texto y no índices: los índices que viajan por el cable son append-only,
         * y una lista de textos deja reordenar el catálogo sin reescribirle el club a nadie.
         */
        this.clubs = new ArraySchema();
        /**
         * El gesto que está haciendo, como índice en `GESTURES`.
         *
         * Sincronizado porque un gesto que sólo ve quien lo hace no es un gesto. Como índice
         * y no como texto por lo mismo que `facing`: un `uint8` contra una cadena por
         * jugador, quince veces por segundo.
         *
         * Lo apaga el SERVIDOR — al caminar, al subirse a algo o al caducar. El cliente
         * nunca escribe aquí, igual que no escribe su posición.
         *
         * Al final de la clase, como manda el contrato binario.
         */
        this.gesture = NO_GESTURE_INDEX;
    }
}
__decorate([
    type("string")
], Player.prototype, "sessionId", void 0);
__decorate([
    type("string")
], Player.prototype, "nickname", void 0);
__decorate([
    type("number")
], Player.prototype, "x", void 0);
__decorate([
    type("number")
], Player.prototype, "y", void 0);
__decorate([
    type("number")
], Player.prototype, "targetX", void 0);
__decorate([
    type("number")
], Player.prototype, "targetY", void 0);
__decorate([
    type("uint8")
], Player.prototype, "color", void 0);
__decorate([
    type("boolean")
], Player.prototype, "walking", void 0);
__decorate([
    type("uint8")
], Player.prototype, "facing", void 0);
__decorate([
    type(["string"])
], Player.prototype, "equipped", void 0);
__decorate([
    type("string")
], Player.prototype, "onElement", void 0);
__decorate([
    type("string")
], Player.prototype, "accountId", void 0);
__decorate([
    type(["string"])
], Player.prototype, "clubs", void 0);
__decorate([
    type("uint8")
], Player.prototype, "gesture", void 0);
export class RoomState extends Schema {
    constructor() {
        super(...arguments);
        /** Which room in the catalog this instance is running. */
        this.roomId = "";
        this.players = new MapSchema();
    }
}
__decorate([
    type("string")
], RoomState.prototype, "roomId", void 0);
__decorate([
    type({ map: Player })
], RoomState.prototype, "players", void 0);
//# sourceMappingURL=state.js.map